import { ARCHIVE_CAMPAIGN, UPDATE_CAMPAIGN } from "@lib/graphql/mutations";
import { CLIENTS_QUERY } from "@lib/graphql/queries";
import { useBreadcrumbStore } from "@lib/stores/breadcrumbStore";
import { CAMPAIGN_STATUSES, STATUS_LABELS } from "@lib/types";
import { Archive, ArrowLeft, Eye, Pencil, Save, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import type { Campaign } from "./context";
import { STAGE_NAMES } from "./context";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  on_hold: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

interface CampaignHeaderProps {
  campaign: Campaign;
  refresh: () => void;
}

function ArchiveHeaderPopover({
  campaignId,
  onDone,
  onCancel,
}: {
  campaignId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [, archiveCampaign] = useMutation(ARCHIVE_CAMPAIGN);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onCancel]);

  async function handleArchive() {
    setSubmitting(true);
    const result = await archiveCampaign({
      input: { campaignId, reason: reason.trim() || null },
    });
    setSubmitting(false);
    if (result.data?.archiveCampaign?.errors?.length === 0) onDone();
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full z-10 mt-1 w-64 rounded-lg border border-border bg-card p-3 shadow-lg"
    >
      <label
        htmlFor="header-archive-reason"
        className="block text-xs font-medium text-text-secondary"
      >
        Reason (optional)
      </label>
      <input
        id="header-archive-reason"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Role filled, on hold"
        className="mt-1 block w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        onKeyDown={(e) => {
          if (e.key === "Enter") handleArchive();
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleArchive}
          disabled={submitting}
          className="rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {submitting ? "Archiving..." : "Archive"}
        </button>
      </div>
    </div>
  );
}

export function CampaignHeader({ campaign, refresh }: CampaignHeaderProps) {
  const [, updateCampaign] = useMutation(UPDATE_CAMPAIGN);
  const setBreadcrumbTail = useBreadcrumbStore((s) => s.setTail);
  const [editing, setEditing] = useState(false);
  const [showArchivePopover, setShowArchivePopover] = useState(false);
  const [editForm, setEditForm] = useState({
    title: campaign.title,
    status: campaign.status,
    clientId: campaign.client.id,
  });
  // Client picker: one large page (field max_page_size 250), not paginated.
  const [{ data: clientsData }] = useQuery({
    query: CLIENTS_QUERY,
    variables: { first: 250 },
    pause: !editing,
  });
  const clients = (clientsData?.clients?.edges ?? []).map(
    (edge: { node: { id: string; name: string } }) => edge.node,
  );

  useEffect(() => {
    setBreadcrumbTail(`${campaign.client.name} · ${campaign.title}`);
    return () => setBreadcrumbTail(null);
  }, [campaign.client.name, campaign.title, setBreadcrumbTail]);

  function startEditing() {
    setEditForm({ title: campaign.title, status: campaign.status, clientId: campaign.client.id });
    setEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const result = await updateCampaign({
      input: {
        id: campaign.id,
        title: editForm.title,
        status: editForm.status,
        clientId: editForm.clientId,
      },
    });
    if (result.data?.updateCampaign?.errors?.length === 0) {
      setEditing(false);
      refresh();
    }
  }

  return (
    <div>
      <Link
        to="/campaigns"
        className="mb-4 flex items-center gap-1 text-sm text-text-muted hover:text-text-secondary"
      >
        <ArrowLeft size={16} /> Back to campaigns
      </Link>

      <div className="flex items-start justify-between gap-4">
        {editing ? (
          <form onSubmit={handleSave} className="flex flex-1 items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-text-muted">Title</label>
              <input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                required
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted">Client</label>
              <select
                value={editForm.clientId}
                onChange={(e) => setEditForm({ ...editForm, clientId: e.target.value })}
                className="mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
              >
                {clients.length === 0 && (
                  <option value={campaign.client.id}>{campaign.client.name}</option>
                )}
                {clients.map((c: { id: string; name: string }) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted">Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
              >
                {CAMPAIGN_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="flex items-center gap-1 rounded bg-accent px-3 py-2 text-xs font-medium text-white hover:bg-accent/90"
            >
              <Save size={14} /> Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-1 rounded border border-border px-3 py-2 text-xs font-medium text-text-muted hover:bg-card-hover"
            >
              <X size={14} /> Cancel
            </button>
          </form>
        ) : (
          <>
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-bold tracking-tight text-text-primary">
                {campaign.title}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-muted">
                <span>{campaign.client.name}</span>
                {campaign.client.industry && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{campaign.client.industry}</span>
                  </>
                )}
                <span aria-hidden="true">·</span>
                <span>
                  Stage {campaign.currentStage}: {STAGE_NAMES[campaign.currentStage - 1]}
                </span>
                <span aria-hidden="true">·</span>
                <span>{campaign.candidateCount} candidates</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}
              >
                {STATUS_LABELS[campaign.status] || campaign.status}
              </span>
              <button
                type="button"
                onClick={startEditing}
                className="rounded border border-border p-1.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                title="Edit campaign"
              >
                <Pencil size={14} />
              </button>
              <a
                href={`/portal/campaigns/${campaign.id}`}
                className="rounded border border-border p-1.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                title="View client portal"
              >
                <Eye size={14} />
              </a>
              {!campaign.archivedAt && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowArchivePopover(!showArchivePopover)}
                    className="rounded border border-border p-1.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    title="Archive campaign"
                  >
                    <Archive size={14} />
                  </button>
                  {showArchivePopover && (
                    <ArchiveHeaderPopover
                      campaignId={campaign.id}
                      onDone={() => {
                        setShowArchivePopover(false);
                        refresh();
                      }}
                      onCancel={() => setShowArchivePopover(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
